import { PageResponse } from "@/types/http.type";
import {
  ClienteAtendimentoDTO,
  ClienteDTO,
  CreateClientePayload,
  UpdateClientePayload,
} from "@/types/cliente.type";
import {
  ClienteFilialDTO,
  SetClienteToClienteFilialPayload,
  UpdateClienteFilialPayload,
} from "@/types/cliente-filial.type";
import { buildQueryParams } from "@/utils/build-query-params";
import { httpClient } from "./httpClient";
import {
  ClienteAtendimentoFiltrosCriteria,
  ClienteAtendimentoFiltrosOptions,
  ClienteFiltrosCriteria,
  ClienteFiltrosOptions,
} from "@workflow/types";

class ClienteService {
  private readonly basePath = "/api/v1/clientes";

  private readonly atendimentoPath = "/api/v1/atendimento/clientes";

  // Atualizado para refletir o path do controller no backend
  private readonly filialPath = "/api/v1/clientes-filiais";

  // =========================================================================
  // CLIENTES (MATRIZ)
  // =========================================================================

  public async getResumoTotais() {
    return await httpClient.get<{
      totalClientes: number;
      filiaisPendentes: number;
      totalFiliais: number;
    }>(`${this.basePath}/resumo-totais`);
  }

  public async getClienteById(id: number) {
    return await httpClient.get<ClienteDTO>(`${this.basePath}/${id}`);
  }

  public async getFiltrosCliente() {
    return await httpClient.get<ClienteFiltrosOptions>(`${this.basePath}/filtros`);
  }

  public async getAllActiveClientes() {
    return await httpClient.get<ClienteDTO[]>(`${this.basePath}/all-active`);
  }

  public async listClientes(filter?: ClienteFiltrosCriteria) {
    let url = `${this.basePath}`;

    if (filter) {
      const query = buildQueryParams(filter);
      if (query) url += "?" + query;
    }

    return await httpClient.get<PageResponse<ClienteDTO>>(url);
  }

  // =========================================================================
  // ATENDIMENTO — listagem somente-leitura (leva ao Painel 360), com jobsCount
  // =========================================================================

  public async listClientesAtendimento(filter?: ClienteAtendimentoFiltrosCriteria) {
    let url = `${this.atendimentoPath}`;

    if (filter) {
      const query = buildQueryParams(filter);
      if (query) url += "?" + query;
    }

    return await httpClient.get<PageResponse<ClienteAtendimentoDTO>>(url);
  }

  public async getFiltrosClienteAtendimento() {
    return await httpClient.get<ClienteAtendimentoFiltrosOptions>(
      `${this.atendimentoPath}/filtros`,
    );
  }

  public async createCliente(payload: CreateClientePayload) {
    return await httpClient.post<ClienteDTO>(this.basePath, payload);
  }

  public async updateCliente(id: number, payload: UpdateClientePayload) {
    return await httpClient.put<ClienteDTO>(`${this.basePath}/${id}`, payload);
  }

  // Obs: Se você for refatorar o backend de Clientes depois,
  // vale a pena trocar esses de post() para patch() lá no Nest e aqui também!
  public async reactivateCliente(id: number) {
    return await httpClient.post<void>(`${this.basePath}/${id}/reativar`);
  }

  public async inactivateCliente(id: number) {
    return await httpClient.post<void>(`${this.basePath}/${id}/inativar`);
  }

  // =========================================================================
  // FILIAIS
  // =========================================================================

  public async getFiltrosFilial() {
    // Substitua 'any' pelo tipo correto do DTO de opções, caso você tenha criado um
    return await httpClient.get<any>(`${this.filialPath}/filtros`);
  }

  public async getFilialById(id: number) {
    return await httpClient.get<ClienteFilialDTO>(`${this.filialPath}/${id}`);
  }

  public async listFiliais(filter?: Record<string, unknown>) {
    let url = `${this.filialPath}`;

    if (filter) {
      const query = buildQueryParams(filter);
      if (query) url += "?" + query;
    }

    return await httpClient.get<PageResponse<ClienteFilialDTO>>(url);
  }

  public async listFiliaisPendentes(filter?: Record<string, unknown>) {
    let url = `${this.filialPath}/pendentes`;

    if (filter) {
      const query = buildQueryParams(filter);
      if (query) url += "?" + query;
    }

    return await httpClient.get<PageResponse<ClienteFilialDTO>>(url);
  }

  public async listFiliaisByClienteId(clienteId: number) {
    return await httpClient.get<ClienteFilialDTO[]>(`${this.filialPath}/cliente/${clienteId}`);
  }

  public async updateFilial(id: number, payload: UpdateClienteFilialPayload) {
    return await httpClient.patch<ClienteFilialDTO>(`${this.filialPath}/${id}`, payload);
  }

  public async setClienteToFilial(id: number, payload: SetClienteToClienteFilialPayload) {
    return await httpClient.patch<ClienteFilialDTO>(`${this.filialPath}/${id}/cliente`, payload);
  }

  public async inactivateFilial(id: number) {
    return await httpClient.patch<void>(`${this.filialPath}/${id}/inativar`);
  }

  public async reactivateFilial(id: number) {
    return await httpClient.patch<void>(`${this.filialPath}/${id}/reativar`);
  }
}

export const clienteService = new ClienteService();
